window.customElements.define(
  'custom-sticky-add-to-cart',
  class CustomStickyAddToCart extends window.HTMLElement {
    connectedCallback() {
      this.handleBuyOnceSubmit = this.handleBuyOnceSubmit.bind(this);
      this.initDOMElements();
      this.initEventListeners();
      this.initVisibilityObserver();

      if (this.form) {
        this.handleUpdateUI(this.getCurrentVariantData());
      }

      this.updateBuyOnceVisibility();
    }

    disconnectedCallback() {
      this.removeEventListeners();
      this.removeVisibilityObserver();
    }

    // Initialize DOM elements
    initDOMElements() {
      this.form = this.querySelector('#sticky-add-to-cart-form');
      this.stickyVariantLabel = this.querySelector('#sticky-variant-label');
      this.stickyVariantSelect = this.querySelector('#sticky-variant-select');
      this.stickyAddToCartPrice = this.querySelector('#sticky-add-to-cart-price');
      this.stickyAddToCartCompareAtPrice = this.querySelector('#sticky-add-to-cart-compare-at-price');
      this.stickyAddToCartButton = this.querySelector('#sticky-add-to-cart-button');
      this.stickyNotifyMeButton = this.querySelector('#sticky-notify-me-button');
      this.buyOnceForm = this.querySelector('#sticky-buy-once-form');
      this.buyOncePrice = this.querySelector('#sticky-buy-once-price');
      this.productForm = document.querySelector('.custom-variant-picker__form');

      if (!this.productForm) {
        return;
      }
    }

    // Event Listeners
    initEventListeners() {
      this.form.addEventListener('change', this.handleChange.bind(this));
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.buyOnceForm?.addEventListener('submit', this.handleBuyOnceSubmit);

      window.addEventListener('custom-variant-picker:change', this.handleVariantPickerChange.bind(this));

      this.pickerToggle = this.productForm?.closest('custom-variant-picker-toggle');
      this.handlePickerToggleClick = this.handlePickerToggleClick.bind(this);
      this.pickerToggle?.addEventListener('click', this.handlePickerToggleClick);
    }

    removeEventListeners() {
      this.form.removeEventListener('change', this.handleChange.bind(this));
      this.form.removeEventListener('submit', this.handleSubmit.bind(this));
      this.buyOnceForm?.removeEventListener('submit', this.handleBuyOnceSubmit);

      window.removeEventListener('custom-variant-picker:change', this.handleVariantPickerChange.bind(this));
      this.pickerToggle?.removeEventListener('click', this.handlePickerToggleClick);
    }

    handlePickerToggleClick(event) {
      if (!event.target.closest('.custom-variant-picker-toggle__swatch-item')) return;
      this.handleUpdateUI(this.getCurrentVariantData());
      this.updateBuyOnceVisibility();
    }

    // Visibility Observer
    initVisibilityObserver() {
      if (!this.productForm) return;

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.classList.add('hidden');
            } else {
              const formRect = this.productForm.getBoundingClientRect();
              if (formRect.top < 0) {
                this.classList.remove('hidden');
              }
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px',
        }
      );

      this.visibilityObserver.observe(this.productForm);
    }

    removeVisibilityObserver() {
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
      }
    }

    // Handlers
    handleVariantPickerChange(event) {
      const { variant } = event.detail;

      if (!variant.variantId) return;
      const variantSelectOption = this.form.querySelector(`select option[value="${variant.variantId}"]`);

      if (!variantSelectOption) return;
      variantSelectOption.selected = true;

      const currentVariantData = this.getCurrentVariantData();
      this.handleUpdateUI(currentVariantData);
      this.handleUpdateUrl(currentVariantData);
      this.updateBuyOnceVisibility();
    }

    handleChange(event) {
      const currentVariantData = this.getCurrentVariantData();

      this.handleUpdateUI(currentVariantData);
      this.handleUpdateUrl(currentVariantData);
      this.updateBuyOnceVisibility();

      this.dispatchEvent(
        new CustomEvent('custom-sticky-add-to-cart:change', {
          detail: {
            variant: {
              price: currentVariantData.price,
              variantId: currentVariantData.variantId,
              sellingPlanId: currentVariantData.sellingPlanId,
              compareAtPrice: currentVariantData.compareAtPrice,
              subscriptionPrice: currentVariantData.subscriptionPrice,
            },
            element: event.target,
          },
          bubbles: true,
        })
      );
    }

    async handleSubmit(event) {
      event.preventDefault();
      const currentVariantData = this.getCurrentVariantData();

      try {
        this.form.classList.add('is-loading');
        const isSubscription = this.isSubscriptionMode();
        await this.addToCart({
          id: currentVariantData.variantId,
          quantity: 1,
          ...(isSubscription && { selling_plan: currentVariantData.sellingPlanId }),
        });

        this.dispatchCartRefresh();
      } catch (error) {
        console.error('Failed to add subscription to cart:', error);
      } finally {
        this.form.classList.remove('is-loading');
      }
    }

    async handleBuyOnceSubmit(event) {
      event.preventDefault();
      const currentVariantData = this.getCurrentVariantData();

      try {
        this.buyOnceForm.classList.add('is-loading');
        await this.addToCart({
          id: currentVariantData.variantId,
          quantity: 1,
        });

        this.dispatchCartRefresh();
      } catch (error) {
        console.error('Failed to add item to cart:', error);
      } finally {
        this.buyOnceForm.classList.remove('is-loading');
      }
    }

    isSubscriptionMode() {
      const pickerRoot = this.pickerToggle || this.productForm?.closest('custom-variant-picker-toggle');
      const subscribeSwatch = pickerRoot?.querySelector('#swatch-item-subscribe-and-save');
      const oneTimeSwatch = pickerRoot?.querySelector('#swatch-item-one-time-purchase');
      return !subscribeSwatch || !oneTimeSwatch || subscribeSwatch.classList.contains('active');
    }

    setMainPrice(price) {
      if (this.stickyAddToCartPrice) {
        this.stickyAddToCartPrice.textContent = price;
      }
    }

    setCompareAtPrice(compareAtPrice, mainPrice) {
      if (!this.stickyAddToCartCompareAtPrice) return;

      if (compareAtPrice && compareAtPrice !== mainPrice) {
        this.stickyAddToCartCompareAtPrice.textContent = compareAtPrice;
        this.stickyAddToCartCompareAtPrice.classList.remove('hidden');
      } else {
        this.stickyAddToCartCompareAtPrice.classList.add('hidden');
      }
    }

    handleUpdateUI(currentVariantData) {
      const mainPrice = this.isSubscriptionMode()
        ? currentVariantData.subscriptionPrice
        : currentVariantData.price;

      this.setMainPrice(mainPrice);
      this.setCompareAtPrice(currentVariantData.compareAtPrice, mainPrice);

      if (this.stickyVariantLabel) {
        this.stickyVariantLabel.textContent = currentVariantData.currentLabel;
      }

      if (this.buyOncePrice && currentVariantData.price) {
        this.buyOncePrice.textContent = currentVariantData.price;
      }

      if (this.stickyAddToCartButton) {
        this.stickyAddToCartButton.classList.toggle('hidden', currentVariantData.available !== 'true');
      }

      if (this.stickyNotifyMeButton) {
        this.stickyNotifyMeButton.classList.toggle('hidden', currentVariantData.available === 'true');
      }
    }

    updateBuyOnceVisibility() {
      if (!this.buyOnceForm) return;

      const currentVariantData = this.getCurrentVariantData();
      const isAvailable = currentVariantData.available === 'true';

      this.buyOnceForm.classList.toggle('hidden', !this.isSubscriptionMode() || !isAvailable);
    }

    handleUpdateUrl(currentVariantData) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', currentVariantData.variantId);
      window.history.replaceState({}, '', url);
    }

    // Utility functions
    async addToCart(item) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: [item] }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      const result = await response.json();

      return result;
    }

    dispatchCartRefresh() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('variant:add'));
    }

    getCurrentVariantData() {
      const currentVariant = this.form.querySelector('select option:checked');
      const { variantId, sellingPlanId, subscriptionPrice, compareAtPrice, savings, price, currentLabel, available } =
        currentVariant.dataset;

      return {
        available,
        variantId,
        sellingPlanId,
        subscriptionPrice,
        compareAtPrice,
        savings,
        price,
        currentLabel,
      };
    }
  }
);
